import { useCallback } from "react";
import type {
  AdvisorMovement,
  Expense,
  ExpenseStatus,
  FundKey,
  FundMovement,
  InventoryItem,
  Loan,
  StockEntry,
} from "@/types";
import type {
  AdvisorDeliveryInput,
  EncargoDeliveryInput,
  PurchaseToStockInput,
  RestockToAdvisorInput,
  StoreCtx,
} from "@/store/appTypes";
import {
  saveAdvisorMovement,
  saveExpense,
  saveInventory,
  saveLoan,
  saveNotification,
  saveSettings,
  saveStockEntry,
  updateEncargoDelivery,
} from "@/store/api";
import { buildFunds, costAdvisor } from "@/utils/accounting";
import { formatCOP, uid } from "@/utils/format";

// Acciones que mueven mercancía e impactan inventario + contabilidad + cuenta del
// asesor: entrega a asesor, compra para stock, reposición y entrega de encargo.
export const useFulfillmentActions = (ctx: StoreCtx) => {
  const { data, setData, run } = ctx;

  // Mueve mercancía hacia/desde un asesor: consignación, venta o devolución.
  const deliverToAdvisor = useCallback(
    (input: AdvisorDeliveryInput) =>
      run(async () => {
        const base: InventoryItem =
          data.inventory.find((i) => i.productId === input.productId) ?? {
            productId: input.productId,
            stockCasa: 0,
            stockPuntoClear: 0,
            stockByAdvisor: {},
            stockOwnedByAdvisor: {},
          };
        const next: InventoryItem = {
          ...base,
          stockByAdvisor: { ...base.stockByAdvisor },
          stockOwnedByAdvisor: { ...base.stockOwnedByAdvisor },
        };
        const q = input.qty;

        if (input.mode === "consignacion") {
          if (input.from === "clear") next.stockPuntoClear = Math.max(0, next.stockPuntoClear - q);
          else next.stockCasa = Math.max(0, next.stockCasa - q);
          next.stockByAdvisor[input.advisorId] =
            (next.stockByAdvisor[input.advisorId] ?? 0) + q;
        } else if (input.mode === "compra") {
          if (input.from === "clear") next.stockPuntoClear = Math.max(0, next.stockPuntoClear - q);
          else next.stockCasa = Math.max(0, next.stockCasa - q);
          next.stockOwnedByAdvisor[input.advisorId] =
            (next.stockOwnedByAdvisor[input.advisorId] ?? 0) + q;
        } else {
          next.stockByAdvisor[input.advisorId] = Math.max(
            0,
            (next.stockByAdvisor[input.advisorId] ?? 0) - q
          );
          next.stockCasa += q;
        }

        const savedInv = await saveInventory(next);
        const savedMove = await saveAdvisorMovement({
          id: "",
          advisorId: input.advisorId,
          productId: input.productId,
          productName: input.productName,
          kind: input.mode,
          qty: q,
          unitValue: input.unitValue,
          amount: input.unitValue * q,
          note: "",
          date: new Date().toISOString(),
        });

        // Nota contable (F3): mover mercancía hacia un asesor NO registra ninguna
        // compra. La mercancía ya era inventario del admin. La consignación es
        // TENENCIA del asesor y la venta al asesor es su DEUDA: aquí no nace un
        // gasto. La compra se registra solo cuando de verdad se compra
        // ("Comprar para stock") o al pagar un encargo.
        const notice = await saveNotification({
          audience: "asesor",
          title:
            input.mode === "consignacion"
              ? "Nueva mercancía en consignación"
              : input.mode === "compra"
              ? "Compra registrada"
              : "Devolución registrada",
          message: `${input.productName} · ${q} und`,
          kind: "info",
          date: new Date().toISOString(),
          read: false,
          link: "/asesor/inventario",
        });

        setData((p) => ({
          ...p,
          inventory: p.inventory.some((x) => x.productId === savedInv.productId)
            ? p.inventory.map((x) => (x.productId === savedInv.productId ? savedInv : x))
            : [...p.inventory, savedInv],
          advisorMovements: [savedMove, ...p.advisorMovements],
          expenses: p.expenses,
          notifications: p.notifications.some((x) => x.id === notice.id)
            ? p.notifications
            : [notice, ...p.notifications],
        }));
      }),
    [run, data.inventory, data.products]
  );

  // Compra del admin para ingresar a stock (F2): en una sola acción deja la
  // compra registrada en Contabilidad (kind 'compra') y suma las unidades a
  // Oficina. El origen de pago puede ser Caja Menor (fondo), un Préstamo de Juank
  // o dejarla pendiente para pagarla luego desde los fondos.
  const addPurchaseToStock = useCallback(
    (input: PurchaseToStockInput) =>
      run(async () => {
        const product = data.products.find((p) => p.id === input.productId);
        if (!product) throw new Error("Producto no encontrado.");
        const qty = Math.max(1, Number(input.qty) || 0);
        const unit = input.unitCost > 0 ? Number(input.unitCost) : product.costBase;
        const amount = unit * qty;
        const now = new Date().toISOString();

        // Candado de Caja Menor: si se paga con caja, debe alcanzar. Si no, no se
        // permite quedar en negativo: hay que pedir un préstamo a Juank primero.
        if (input.payment === "caja") {
          const caja = buildFunds(data).funds.find((f) => f.key === "caja")?.balance ?? 0;
          if (amount > caja) {
            throw new Error(
              `La Caja Menor no tiene fondos suficientes (disponible ${formatCOP(caja)}). Pide un préstamo a Juank para cubrir esta compra.`
            );
          }
        }

        // 1) Inventario: suma las unidades a Oficina (stock del admin).
        const baseInv: InventoryItem =
          data.inventory.find((i) => i.productId === input.productId) ?? {
            productId: input.productId,
            stockCasa: 0,
            stockPuntoClear: 0,
            stockByAdvisor: {},
            stockOwnedByAdvisor: {},
          };
        const savedInv = await saveInventory({
          ...baseInv,
          stockCasa: baseInv.stockCasa + qty,
        });

        // 2) Compra en Contabilidad. Si se paga de una vez queda pagada; si no,
        //    queda pendiente para pagarla luego desde los fondos.
        let paidAmount = 0;
        let status: ExpenseStatus = "pendiente";
        let fund: FundKey | null = null;
        if (input.payment === "caja" || input.payment === "prestamo") {
          paidAmount = amount;
          status = "pagado";
          fund = "caja";
        }
        const savedExpense = await saveExpense({
          id: "",
          date: now,
          concept: `Compra para stock · ${product.name} (${qty} und)`,
          category: input.category,
          wallet: input.wallet,
          amount,
          note: "Compra para ingresar a Oficina",
          kind: "compra",
          status,
          paidAmount,
          fund,
          productId: product.id,
        });

        // 3) Préstamo de Juank: inyecta liquidez a la Caja Menor y queda como deuda.
        let loan: Loan | null = null;
        let nextSettings = data.settings;
        if (input.payment === "prestamo") {
          loan = await saveLoan({
            id: "",
            date: now,
            concept: `Préstamo para compra: ${product.name}`,
            value: amount,
            type: "Préstamo",
          });
          const movement: FundMovement = {
            id: uid("fm"),
            date: now,
            kind: "prestamo_juank",
            fromFund: null,
            toFund: "caja",
            amount,
            note: `Préstamo de Juank para compra de ${product.name}`,
          };
          nextSettings = {
            ...data.settings,
            fundMovements: [...data.settings.fundMovements, movement],
          };
          await saveSettings(nextSettings);
        }

        // 4) PV: al entrar mercancía al stock propio se suman los Puntos de
        //    Volumen del producto. Las ventas NO vuelven a sumar PV.
        const stockEntry = await saveStockEntry({
          id: "",
          date: now,
          productId: product.id,
          productName: product.name,
          qty,
          pvPerUnit: product.pv,
          pvTotal: product.pv * qty,
          source: "compra",
          note: "Compra para stock",
        });

        // Aviso al admin: la compra quedó pendiente de pago (se cancela en Contabilidad).
        const pendingNotice =
          status !== "pagado"
            ? await saveNotification({
                audience: "admin",
                title: "Compra pendiente por pagar",
                message: `${product.name} (${qty} und) · ${formatCOP(amount)}`,
                kind: "order",
                date: now,
                read: false,
                link: "/admin/contabilidad",
              })
            : null;

        setData((p) => ({
          ...p,
          inventory: p.inventory.some((x) => x.productId === savedInv.productId)
            ? p.inventory.map((x) => (x.productId === savedInv.productId ? savedInv : x))
            : [...p.inventory, savedInv],
          expenses: [savedExpense, ...p.expenses],
          loans: loan ? [loan, ...p.loans] : p.loans,
          settings: nextSettings,
          stockEntries: [stockEntry, ...p.stockEntries],
          notifications: pendingNotice
            ? [pendingNotice, ...p.notifications]
            : p.notifications,
        }));
      }),
    [run, data]
  );

  // F6 — Reposición completa: en una sola operación (a) compra las unidades que
  // falten y las ingresa a Oficina, y (b) las deja en consignación al asesor. Si ya
  // hay stock suficiente, solo asigna (sin comprar nada).
  const restockToAdvisor = useCallback(
    (input: RestockToAdvisorInput) =>
      run(async () => {
        const product = data.products.find((p) => p.id === input.productId);
        if (!product) throw new Error("Producto no encontrado.");
        const advisor = data.distributors.find((d) => d.id === input.advisorId);
        const qty = Math.max(1, Number(input.qty) || 0);
        const now = new Date().toISOString();

        const baseInv: InventoryItem =
          data.inventory.find((i) => i.productId === input.productId) ?? {
            productId: input.productId,
            stockCasa: 0,
            stockPuntoClear: 0,
            stockByAdvisor: {},
            stockOwnedByAdvisor: {},
          };
        const stock = baseInv.stockCasa ?? 0;
        const missing = Math.max(0, qty - stock);
        const purchaseAmount = missing * product.costBase;

        // Candado de Caja Menor (misma regla que la compra para stock).
        if (missing > 0 && input.payment === "caja") {
          const caja = buildFunds(data).funds.find((f) => f.key === "caja")?.balance ?? 0;
          if (purchaseAmount > caja) {
            throw new Error(
              `La Caja Menor no tiene fondos suficientes (disponible ${formatCOP(caja)}). Pide un préstamo a Juank para cubrir esta compra.`
            );
          }
        }

        // (a) Compra de lo que falte (queda en Contabilidad como compra).
        let expense: Expense | null = null;
        let loan: Loan | null = null;
        let nextSettings = data.settings;
        if (missing > 0) {
          let paidAmount = 0;
          let status: ExpenseStatus = "pendiente";
          let fund: FundKey | null = null;
          if (input.payment === "caja" || input.payment === "prestamo") {
            paidAmount = purchaseAmount;
            status = "pagado";
            fund = "caja";
          }
          expense = await saveExpense({
            id: "",
            date: now,
            concept: `Compra reposición · ${product.name} (${missing} und)`,
            category: input.category,
            wallet: input.wallet,
            amount: purchaseAmount,
            note: `Reposición para ${input.advisorName}`,
            kind: "compra",
            status,
            paidAmount,
            fund,
            productId: product.id,
          });
          if (input.payment === "prestamo") {
            loan = await saveLoan({
              id: "",
              date: now,
              concept: `Préstamo para reposición: ${product.name}`,
              value: purchaseAmount,
              type: "Préstamo",
            });
            const movement: FundMovement = {
              id: uid("fm"),
              date: now,
              kind: "prestamo_juank",
              fromFund: null,
              toFund: "caja",
              amount: purchaseAmount,
              note: `Préstamo de Juank para reponer ${product.name}`,
            };
            nextSettings = {
              ...data.settings,
              fundMovements: [...data.settings.fundMovements, movement],
            };
            await saveSettings(nextSettings);
          }
        }

        // (b) Inventario: lo comprado entra a Oficina y las unidades salen a
        //     consignación del asesor (sin descuadrar el resto del stock).
        const savedInv = await saveInventory({
          ...baseInv,
          stockCasa: stock + missing - qty,
          stockByAdvisor: {
            ...baseInv.stockByAdvisor,
            [input.advisorId]: (baseInv.stockByAdvisor[input.advisorId] ?? 0) + qty,
          },
        });

        const unitValue = costAdvisor(product.pricePublic, advisor?.margin ?? 25);
        const move = await saveAdvisorMovement({
          id: "",
          advisorId: input.advisorId,
          productId: product.id,
          productName: product.name,
          kind: "consignacion",
          qty,
          unitValue,
          amount: unitValue * qty,
          note: "Reposición",
          date: now,
        });

        // PV: solo la mercancía NUEVA que se compra para reponer suma puntos
        // (la que ya había en Oficina ya sumó cuando entró).
        const stockEntry =
          missing > 0
            ? await saveStockEntry({
                id: "",
                date: now,
                productId: product.id,
                productName: product.name,
                qty: missing,
                pvPerUnit: product.pv,
                pvTotal: product.pv * missing,
                source: "reposicion",
                note: `Reposición para ${input.advisorName}`,
              })
            : null;

        const notice = await saveNotification({
          audience: "asesor",
          title: "Nueva mercancía en consignación",
          message: `${product.name} · ${qty} und`,
          kind: "info",
          date: now,
          read: false,
          link: "/asesor/inventario",
        });

        // Aviso al admin cuando la reposición generó una compra pendiente de pago.
        const pendingNotice =
          expense && expense.status !== "pagado"
            ? await saveNotification({
                audience: "admin",
                title: "Compra pendiente por pagar",
                message: `${product.name} (${missing} und) · reposición`,
                kind: "order",
                date: now,
                read: false,
                link: "/admin/contabilidad",
              })
            : null;

        setData((p) => ({
          ...p,
          inventory: p.inventory.some((x) => x.productId === savedInv.productId)
            ? p.inventory.map((x) => (x.productId === savedInv.productId ? savedInv : x))
            : [...p.inventory, savedInv],
          advisorMovements: [move, ...p.advisorMovements],
          expenses: expense ? [expense, ...p.expenses] : p.expenses,
          loans: loan ? [loan, ...p.loans] : p.loans,
          settings: nextSettings,
          stockEntries: stockEntry ? [stockEntry, ...p.stockEntries] : p.stockEntries,
          notifications: [
            ...(pendingNotice ? [pendingNotice] : []),
            ...(p.notifications.some((x) => x.id === notice.id)
              ? p.notifications
              : [notice, ...p.notifications]),
          ],
        }));
      }),
    [run, data]
  );

  // F5 — Entrega de un pedido por encargo. Regla dura: NO se entrega sin stock
  // primero en Oficina. El producto sale de Oficina y pasa al inventario del asesor
  // (propio si lo compra, consignación si queda como tenencia TUYA). No se registra
  // un gasto nuevo en la entrega: la compra ya se registró al ingresarlo a stock
  // con "Comprar para stock". El fee logístico (domicilio) se le cobra al asesor.
  const deliverEncargo = useCallback(
    (input: EncargoDeliveryInput) =>
      run(async () => {
        const enc = data.encargos.find((e) => e.id === input.encargoId);
        if (!enc) throw new Error("No se encontró el pedido.");
        // Candado: si el pedido ya tiene una entrega registrada (compra o
        // consignación), no se vuelve a aplicar para no duplicar inventario,
        // cuenta del asesor ni gasto contable.
        if (enc.deliverMode) {
          throw new Error("Este pedido ya tiene una entrega registrada.");
        }

        const advisor = data.distributors.find((d) => d.id === enc.advisorId);
        const product = data.products.find((p) => p.id === enc.productId);
        const margin = advisor?.margin ?? 25;
        const costBase = product?.costBase ?? 0;
        const advisorUnitCost = product
          ? Math.round(product.pricePublic * (1 - margin / 100))
          : enc.unitPrice;
        const qty = enc.qty;
        const fee = enc.logisticsFee;
        const now = new Date().toISOString();

        // 1) Inventario (F5): no se entrega nada que no esté primero en Oficina.
        const baseInv: InventoryItem =
          data.inventory.find((i) => i.productId === enc.productId) ?? {
            productId: enc.productId,
            stockCasa: 0,
            stockPuntoClear: 0,
            stockByAdvisor: {},
            stockOwnedByAdvisor: {},
          };
        if (baseInv.stockCasa < qty) {
          throw new Error(
            `No hay stock suficiente en Oficina (${baseInv.stockCasa} und). Compra el producto primero para ingresarlo a stock y luego entrégalo.`
          );
        }
        const nextInv: InventoryItem = {
          ...baseInv,
          stockCasa: baseInv.stockCasa - qty,
          stockByAdvisor: { ...baseInv.stockByAdvisor },
          stockOwnedByAdvisor: { ...baseInv.stockOwnedByAdvisor },
        };
        if (input.mode === "compra") {
          nextInv.stockOwnedByAdvisor[enc.advisorId] =
            (nextInv.stockOwnedByAdvisor[enc.advisorId] ?? 0) + qty;
        } else {
          nextInv.stockByAdvisor[enc.advisorId] =
            (nextInv.stockByAdvisor[enc.advisorId] ?? 0) + qty;
        }
        const savedInv = await saveInventory(nextInv);

        const newMovements: AdvisorMovement[] = [];

        // 3) Movimiento de la mercancía entregada.
        const goods = await saveAdvisorMovement({
          id: "",
          advisorId: enc.advisorId,
          productId: enc.productId,
          productName: enc.productName,
          kind: input.mode === "compra" ? "compra" : "consignacion",
          qty,
          unitValue: advisorUnitCost,
          amount: advisorUnitCost * qty,
          note:
            input.mode === "compra"
              ? "Encargo entregado (compra pagada)"
              : "Encargo entregado en consignación",
          date: now,
        });
        newMovements.push(goods);

        // 4) Compra: queda PAGADA de contado (se registra el abono y la deuda queda en 0).
        if (input.mode === "compra" && advisorUnitCost * qty > 0) {
          const payment = await saveAdvisorMovement({
            id: "",
            advisorId: enc.advisorId,
            productId: enc.productId,
            productName: enc.productName,
            kind: "pago",
            qty: 0,
            unitValue: advisorUnitCost * qty,
            amount: advisorUnitCost * qty,
            note: "Pago de la compra del encargo",
            date: now,
          });
          newMovements.push(payment);
        }

        // 5) Fee logístico: se le cobra al asesor (suma a lo que debe).
        if (fee > 0) {
          const feeMove = await saveAdvisorMovement({
            id: "",
            advisorId: enc.advisorId,
            productId: "",
            productName: "Fee logístico",
            kind: "fee",
            qty: 0,
            unitValue: fee,
            amount: fee,
            note: `Fee del encargo: ${enc.productName}`,
            date: now,
          });
          newMovements.push(feeMove);
        }

        // 6) Marco el pedido como entregado y guardo gasto/ganancia/descuento.
        const savedEnc = await updateEncargoDelivery(enc.id, {
          mode: input.mode,
          wallet: null,
          unitCostBase: costBase,
          unitCostAdvisor: advisorUnitCost,
        });

        // 7) Aviso al asesor.
        const notice = await saveNotification({
          audience: "asesor",
          title:
            input.mode === "compra"
              ? "Compra de encargo registrada"
              : "Encargo entregado en consignación",
          message: `${qty} x ${enc.productName}`,
          kind: "info",
          date: now,
          read: false,
          link: "/asesor/encargo",
        });

        setData((p) => ({
          ...p,
          encargos: p.encargos.map((x) => (x.id === enc.id ? savedEnc : x)),
          inventory: p.inventory.some((x) => x.productId === savedInv.productId)
            ? p.inventory.map((x) => (x.productId === savedInv.productId ? savedInv : x))
            : [...p.inventory, savedInv],
          advisorMovements: [...newMovements, ...p.advisorMovements],
          notifications: p.notifications.some((x) => x.id === notice.id)
            ? p.notifications
            : [notice, ...p.notifications],
        }));
      }),
    [run, data.encargos, data.distributors, data.products, data.inventory]
  );

  return { deliverToAdvisor, addPurchaseToStock, restockToAdvisor, deliverEncargo };
};